import QRCode from 'qrcode';
import { useCallback, useEffect, useRef, useState } from 'react';

import type {
  ArrivalRow,
  ArrivalsFeed,
  AttendanceResponse,
  ChoirBrief,
  CurrentSession,
  MyAttendance,
  MyMark,
  PeerCodeResponse,
} from '@contracts/index';

import { ApiError, api } from '../api';
import {
  failureText,
  formatLongDate,
  formatTime,
  hasLeeway,
  plural,
  voicePartLetter,
  words,
} from '../format';
import { useFold } from '../fold';
import { ExpandIcon } from '../icons';
import { tokenFromScannedText } from '../launch';
import { usePoll } from '../liveness';
import { haptic, scanQr } from '../telegram';
import CodeInput from './CodeInput';
import Avatar, { AVATAR_CARD, AVATAR_FEED, smileOf } from './Avatar';
import { livenessPhase, useGestures, type LiveFace } from './faceLiveness';
import { rungJustReached } from './ladder';

const NUMERIC_LENGTH = 6;

/**
 * The wait after too many attempts, counted down on screen.
 *
 * The server sends the seconds remaining and the rule of this product is that
 * every wait is named by a number. A figure that stands still for two minutes
 * is a number nobody trusts, so it ticks: «2:00» becomes «1:59» and the person
 * can see that waiting is working.
 */
function Lockout({ seconds, attempts }: { seconds: number; attempts: number }) {
  const [left, setLeft] = useState(seconds);
  useEffect(() => {
    setLeft(seconds);
    const timer = window.setInterval(() => setLeft((value) => Math.max(0, value - 1)), 1000);
    return () => window.clearInterval(timer);
  }, [seconds]);
  const minutes = Math.floor(left / 60);
  const rest = String(left % 60).padStart(2, '0');
  return (
    <section className="refusal card--toned card--toned--outline" data-tone="late">
      <p className="label label--tone">
        Код не подошёл · {attempts} {plural(attempts, ['попытка', 'попытки', 'попыток'])}
      </p>
      <p className="figure figure--answer figure--tone refusal-timer">
        {minutes}:{rest}
      </p>
      <p className="refusal-note">
        {left === 0
          ? 'Можно пробовать снова. Спроси цифры у регента ещё раз — код меняется каждые полминуты.'
          : 'Подожди, пока пройдёт время. Спроси цифры у регента ещё раз — код меняется каждые полминуты.'}
      </p>
    </section>
  );
}

/**
 * The whole answer in one line: «Вовремя, в 18:03» or «Опоздание 12 мин».
 *
 * The time is the fact the singer is holding the phone to see, so it comes with
 * the verdict rather than under it. Without a time the mark is still a mark -
 * the regent can set one by hand and type no time - and the sentence simply
 * stops earlier rather than trailing off after «в ».
 */
function markedFact(mark: MyMark): string {
  const time = mark.marked_at === null ? null : formatTime(mark.marked_at);
  if (mark.status === 'late') {
    const minutes = mark.late_minutes === null ? null : `${mark.late_minutes} мин`;
    if (minutes === null) return time === null ? 'Опоздание' : `Опоздание, в ${time}`;
    return time === null ? `Опоздание ${minutes}` : `Опоздание ${minutes}, в ${time}`;
  }
  return time === null ? 'Вовремя' : `Вовремя, в ${time}`;
}

/**
 * The card announcing a mark carries the colour of what the mark says.
 *
 * It was green whatever happened, so «Опоздание 15 мин» arrived in the colour
 * that means «вовремя» — the one thing colour is not allowed to do here.
 */
/**
 * The singer's own figures, under the mark that has just been made.
 *
 * The one screen a singer opens during a gathering ends with what checking in
 * was for. Two monospaced lines and a heading, the same shape «Я» uses for the
 * choir's own figures: here they are a footnote to the mark above, and a
 * headline score under a card that already carries one would be two headlines.
 */
function Tally({ stats }: { stats: MyAttendance }) {
  return (
    <section className="figures">
      <h2 className="figures-title">Моя посещаемость</h2>
      <p className="figures-line">
        {stats.attended}/{stats.total} {plural(stats.total, words(stats.kind).counted)} с{' '}
        {formatLongDate(stats.period_start)}
        {stats.streak > 0 && (
          <>
            {' · '}
            {/* The strict rule's wording: attendance, not punctuality. */}
            серия {stats.streak}
          </>
        )}
      </p>
    </section>
  );
}

/**
 * Where the mark came from and what leeway it was measured against.
 *
 * The handoff's own sentence — «Отметка по цифрам · льгота 3 минуты учтена» —
 * and both halves earn their place: the singer who was marked by somebody else
 * should know it, and the grace is the rule that decided «вовремя» rather than
 * «опоздание на две минуты».
 *
 * The tail appears only when the grace actually decided this mark: the person
 * came after the start and was counted on time anyway. Somebody who arrived
 * before the hour was never near the line, and telling them about a rule that
 * did nothing for them is noise. It also keeps the sentence honest under the
 * rule of 2026-09-04, where a gathering opened early has no grace left to
 * spend — there such a mark cannot exist, so the tail never prints.
 */
function markOrigin(mark: MyMark, session: CurrentSession | null): string {
  const origin = mark.by_opening
    ? 'Отметка за открытие'
    : mark.by_regent
      ? 'Отметил регент'
      : 'Отметка по коду';
  const grace = session?.grace_minutes ?? 0;
  if (grace === 0 || mark.status !== 'on_time' || (mark.late_minutes ?? 0) === 0) return origin;
  return `${origin} · льгота ${grace} ${plural(grace, ['минута', 'минуты', 'минут'])} учтена`;
}

/**
 * What the card says under the fact: where the mark was made, and what the
 * streak has become.
 *
 * The streak because it is the thing the check-in just moved, and the one
 * number on this screen that is about the person rather than about the evening.
 * Silent at nought — «серия стала 0» is a sentence nobody needs read to them at
 * the moment they turned up, and silent until the figures arrive, which is a
 * request this screen makes only once a mark exists.
 *
 * Both states of the card carry it: the one this screen's own tap produced and
 * the one a revisit finds. The sentence is true of the rehearsal rather than of
 * the visit, and a person who checked in and came back to look would otherwise
 * find the news gone.
 */
function withStreak(where: string, stats: MyAttendance | null): string {
  if (stats === null || stats.streak === 0) return where;
  return `${where} · серия стала ${stats.streak}`;
}

function streakLine(stats: MyAttendance | null, result: AttendanceResponse): string {
  return withStreak(
    result.already_marked
      ? 'Отметка уже была — ничего не изменилось'
      : (result.session_title ?? result.choir_name),
    stats,
  );
}

// The feed follows the room at the same cadence as the regent's panel: an
// arrival should appear here about when the regent's segment lights up.
const FEED_POLL_MS = 2000;

/**
 * «Кто уже здесь» — the room assembling.
 *
 * The Partiful lesson translated into this system's language: the singer who
 * just checked in leaves the screen with a feeling of a gathering hall, not of
 * a completed transaction. One flat list in the order people arrived, the way
 * the frames draw it - the voice parts are a single line of figures above it
 * rather than five headings inside it, because at this density a heading per
 * part is more furniture than list.
 *
 * Only arrivals, and no flags: this is a room, not a report.
 */
function Arrivals({
  feed,
  myMemberId,
  myStreak,
}: {
  feed: ArrivalsFeed;
  myMemberId: number;
  /** The streak this check-in has just made, for the one row it belongs to. */
  myStreak: number;
}) {
  const rows = feed.rows ?? [];
  /* The same two layers «Хор» has: a phase per face and one scheduler for the
     list. The room assembling is exactly the place a still wall of faces reads
     as a screenshot rather than as people arriving. */
  const faces: LiveFace[] = rows.map((row) => ({
    id: row.member_id,
    smile: smileOf(row.attendance_rate_30d),
    away: false,
  }));
  const { seen, gesture } = useGestures(faces);
  /* A check-in that landed on a rung jumps at once and does not wait its turn
     in the scheduler: it is the one gesture on this screen that answers
     something the person did rather than filling a quiet moment. */
  const mine = rungJustReached(myStreak) === undefined ? null : myMemberId;
  if (rows.length === 0) return null;

  return (
    <div className="arrivals">
      <span className="label">
        Кто уже здесь · <span className="section-count">{feed.present_count}</span>
      </span>
      {/* «С 4 · А 4 · Т 2 · Б 3» — the parts as one line of figures. */}
      <p className="arrivals-parts">
        {feed.parts
          .map((part) => `${voicePartLetter(part.voice_part)} ${part.present}`)
          .join(' · ')}
      </p>
      <div className="arrivals-list">
        {rows.map((row) => (
          <div key={row.member_id} className="arrival-row" ref={seen(row.member_id)}>
            <Avatar
              key={gesture?.id === row.member_id ? `${row.member_id}:${gesture.at}` : row.member_id}
              eyes={row.avatar_eyes}
              color={row.avatar_color}
              rate={row.attendance_rate_30d}
              size={AVATAR_FEED}
              phase={livenessPhase(row.member_id, smileOf(row.attendance_rate_30d))}
              move={
                row.member_id === mine ? 'hop' : gesture?.id === row.member_id ? gesture.move : null
              }
              mine={row.member_id === myMemberId}
              streak={row.member_id === myMemberId ? myStreak : 0}
            />
            <span className="arrival-name">
              {row.display_name}
              {row.member_id === myMemberId && ' · ты'}{' '}
              <span className="live-part">{voicePartLetter(row.voice_part)}</span>
            </span>
            {row.marked_at !== null && (
              <span
                className={
                  row.member_id === myMemberId ? 'arrival-time arrival-time--mine' : 'arrival-time'
                }
              >
                {formatTime(row.marked_at)}
              </span>
            )}
          </div>
        ))}
      </div>
      <p className="arrivals-note">только пришедшие · обновляется само</p>
    </div>
  );
}

const PEER_POLL_MS = 2000;

/**
 * The code a marked singer shows to the next singer.
 *
 * Fifty people and one screen is a queue of minutes at the door, so check-in
 * spreads from the people who came. This is not the rehearsal's code: it is the
 * caller's own, signed with their member_id, and the row it produces records
 * that the arrival came through them. Which is the whole reason the plate can
 * exist at all - a fresh code on fifty phones would leave nothing behind saying
 * whose screen anybody read it off.
 *
 * The same object in two sizes as the regent's panel, and the same fold: one
 * plate that shrinks into the strip and grows back out of it, pulled or tapped.
 * What it has not got is digits. Six of them carry no voucher, so this path is
 * the QR alone, and the middle of the row says what the thing is where the
 * panel puts a figure.
 *
 * The poll rides usePoll, which stops while the app is off screen. That matters
 * more here than on the panel: this runs on every phone in the choir rather than
 * on one, and a singer's app spends the rehearsal minimised.
 */
function PeerPlate() {
  const [code, setCode] = useState<PeerCodeResponse | null>(null);
  const [qr, setQr] = useState<string | null>(null);
  /* 12, where the panel passes 0: this plate stands on a card of its own in
     both states, so what it grows to is the card's width less its inset. */
  const fold = useFold(12);
  /* A refusal stops the asking. The commonest one is the regent moving this
     person to «пропуск» mid-rehearsal, which withdraws their code along with the
     mark it stood on; polling into a refusal twice a second says nothing new. */
  const [refused, setRefused] = useState(false);
  const drawn = useRef<string | null>(null);

  const poll = useCallback(async () => {
    try {
      setCode(await api.peerCode());
    } catch {
      setRefused(true);
    }
  }, []);
  usePoll(poll, PEER_POLL_MS, !refused);

  useEffect(() => {
    if (code === null || drawn.current === code.qr_token) return;
    drawn.current = code.qr_token;
    void QRCode.toDataURL(code.qr_payload, { margin: 1, width: 320 }).then(setQr, () => {});
  }, [code]);

  if (refused || code === null) return null;

  /* Spent rather than gone. The server refuses the fourth arrival through one
     code, so drawing the plate would be offering something that does not work -
     and drawing nothing at all would read as the whole thing having vanished. */
  if (code.vouches_left === 0) {
    return <p className="peer-spent">По твоему коду отметились все, кого можно</p>;
  }

  return (
    <div
      className="code-block code-block--peer"
      ref={fold.block}
      style={fold.styles.block}
      data-press="none"
      role="button"
      tabIndex={0}
      aria-expanded={fold.open}
      {...fold.grip}
    >
      <span className="code-head" style={fold.styles.head}>
        <span ref={fold.head}>
          <span className="code-head-row">
            <span className="label">код для соседа</span>
          </span>
        </span>
      </span>
      <span className="code-qr" style={fold.styles.slot}>
        {qr !== null && (
          <span className="qr" style={fold.styles.plate}>
            <img src={qr} alt="QR со ссылкой на отметку" />
            {fold.hint && (
              <span className="code-hint" style={fold.styles.hint}>
                <ExpandIcon size={14} />
              </span>
            )}
          </span>
        )}
      </span>
      <span className="code-row" style={fold.styles.row}>
        <span className="code-figures">
          {/* What the object is belongs to the strip alone: expanded, the
              heading over the QR has already said the name. How much is left is
              worth reading in both, and it is the same phrase in both, so it is
              one element and not a strip-sized copy beside a plate-sized one. */}
          <span className="code-title" style={fold.styles.title}>
            Код для соседа
          </span>
          {/* «осталось 2 · 24 с» read as two minutes twenty-four seconds: the
              dot used to join two facts here, and the seconds have gone to the
              end of the row where they are printed once. What is left names its
              own unit. */}
          <span className="code-remaining">
            ещё {code.vouches_left} {plural(code.vouches_left, ['человек', 'человека', 'человек'])}
          </span>
        </span>
        <span className="code-countdown">{code.seconds_until_rotation} с</span>
      </span>
      <span className="code-tail" style={fold.styles.tail}>
        <span ref={fold.tail}>
          {/* The sentence that makes the record mean something to the person
              holding the phone. Everything the rotation used to guarantee rests
              on this being read: nobody lends a screen carelessly once their own
              name goes on whatever comes of it. */}
          <span className="code-note">
            Кто отметится по этому коду, будет записан как пришедший по твоему.
          </span>
        </span>
      </span>
      <button
        className="code-handle"
        data-press="none"
        aria-label="Развернуть или свернуть код"
        onClick={fold.toggle}
      >
        <span />
      </button>
    </div>
  );
}

/**
 * **A subset of `MarkScreen` in `legacy/frontend/src/screens/MarkScreen.tsx`, not a fork of it.**
 * F-007 builds it one act at a time and this is the frame, the three screens a mark puts up, and
 * what each of them asks the server for: what stands here is `Lockout` (`:37-69`), `markedFact`
 * (`:71-87`), the orphan comment about the card's own colour and `Tally` (`:89-120`), `markOrigin`,
 * `withStreak` and `streakLine` (`:122-175`), `FEED_POLL_MS` (`:177-179`), `Arrivals` (`:181-272`),
 * `PEER_POLL_MS` and `PeerPlate` (`:274-402`), legacy's `Props` (`:404-418`), all eleven of its
 * pieces of state — the nine `useState`s and the two refs — with `myMemberId` and `marked` beside
 * them (`:426-428`, `:432-438`, `:440-442`, `:447-448`), the feed poll and the stats ask
 * (`:450-472`), the whole of `mark()` (`:474-512`), the token effect (`:514-521`), `handleScan`
 * (`:523-546`), the loading branch (`:548-550`), the head (`:552-572`), the fresh card
 * (`:574-607`) with its `<PeerPlate />` (`:602`), the revisit (`:609-645`) with its own and the
 * comment over it (`:637-640`), the sticky absence (`:647-673`), the entry (`:675-716`, its
 * divider and scanner button `:711-714` included) and the three siblings under it (`:717-719`),
 * in legacy's order, comments included. `scripts/ported-partial.txt` holds every run of it to the
 * old file line for line.
 *
 * **What is still to come, from this same slice**: nothing. T10 closed the file's own work,
 * and F-008b the last flow's (below), so nothing of the old file is absent any more.
 * F-022's T3 put back `Avatar`'s `mine` and `streak` on the three faces
 * (`:247-248`, `:589-590`, `:628-629`), so the bullet that listed them as absent is gone, and
 * with it **AC-181**'s «held by review» note: it held their *absence*, which no longer exists.
 *
 * **F-008a added the prop, the ref and the effect** — `initialToken` (`:416`), `consumedToken`
 * (`:441`) and the token effect (`:514-521`), which check in from a scanned link through the
 * same `mark()`. The ref dies with the screen, so a fresh mount with the same token posts it
 * again — `FND-157`, `KEEP`.
 *
 * **Held by review rather than by a case**, each measured by applying the mutation and
 * watching what goes red (`.claude/rules/testing.md` rule 2):
 *
 *   * `setDigits('')` on the success path (`:483`) — the entry unmounts in the same commit
 *     that sets `result`, so the emptied cells are no longer on screen to be read. Its
 *     sibling at `:505` is held by a case.
 *   * `key` on the row (`:232`), `key` on its `Avatar` (`:234`) and `ref={seen(…)}` (`:232`)
 *     — deleting any of the three leaves the whole suite green. The two keys are React's
 *     bookkeeping over a list whose ids never move, and `seen` feeds an
 *     `IntersectionObserver` the test runner does not have, where `useGestures` counts every
 *     face as visible (`faceLiveness.ts:139-140`). They are ported because the old app has
 *     them, and the baselines of §7 are what look at the list.
 *   * the scheduler's arm of the row's `move` (`:243-245`) — `startGestures` books itself with
 *     a real `setTimeout` of 3-7 s (`faceLiveness.ts:122`), which `toFake` leaves alone, so no
 *     gesture fires inside a case. The `'hop'` arm above it is held by AC-157.
 *
 * **F-008b added the scanner** — `scanning` (`:435-437`), `handleScan` (`:523-546`) and, under
 * the code button, `p.label.divider` «или» with «Сканировать QR камерой» (`:711-714`), with the
 * `tokenFromScannedText` and `scanQr` imports they read (`:27`, `:29`). F-007 left them out as
 * the one approved deviation of that slice, and `FND-145` closes here. The code button and its
 * `disabled={busy || digits.length === 0}` are F-007's and stay. What a reader of those lines
 * needs, each `KEEP`: no scanner, a scanner that threw and a scanner that never answered all
 * print the one sentence, the last after `scanQr`'s own twelve seconds (`FND-218`); neither
 * scanner failure fires a haptic (`FND-221`); the code button's `disabled` omits `scanning` on
 * purpose, so the typed path stays open during a scan and two check-ins can be in flight
 * (`FND-222`); and nothing cancels a scan when the screen unmounts — the timer, the promise and
 * `closeScanQrPopup()` outlive it (`FND-223`).
 *
 * The signature is legacy's line for line (`:420-425`). What below is not legacy's is one name:
 * `result`'s state, `mark()`'s parameter and `streakLine`'s are typed `AttendanceResponse` where
 * legacy writes `MarkResult` (`:427`, `:474`, `:168`): the same shape under the name the contract
 * gives it (`packages/contracts/src/attendance.ts:47`), which is the one place a server shape may
 * be declared — ADR-0011.
 *
 * **F-009 added the plate** — `PEER_POLL_MS` and `PeerPlate` (`:274-402`), the `qrcode`, `useFold`
 * and `ExpandIcon` imports it reads (`:1`, `:25-26`), and its two mounts (`:602`, `:637-640`). Two
 * of its lines are not legacy's: `code`'s state is typed `PeerCodeResponse` where legacy writes
 * `PeerCode` (`:297`, and the import at `:14`), the name the contract gives it
 * (`packages/contracts/src/attendance.ts:83`); and the encoder's `.then` takes a rejection handler
 * legacy has not got (`:320`) — `FND-171`, `FIX`, the clause `FND-149` gave the regent's
 * (`RegentScreen.tsx:145`), so that an unhandled rejection cannot carry `qr_payload` into the
 * error tracker.
 *
 * **The head is written here and in no helper the regent's screen could share.** «ИДЁТ С »
 * and «ИДЁТ · » (`RegentScreen.tsx:209`) are two literals in two files, and `FND-131` was
 * decided with that clause: no component, hook or helper imported by both may render
 * `.live-line`. AC-130.
 */
type Props = {
  /**
   * The rehearsal that is running; undefined while the server has not answered
   * yet. Never null: with nothing running there is no check-in screen at all,
   * and the tab is the schedule - see SessionsScreen.
   */
  session: CurrentSession | undefined;
  /**
   * Whose screen this is. The feed marks one row «· ты», and the card that says
   * the mark is made carries this person's own face at the left of it.
   */
  choir: ChoirBrief;
  initialToken: string | null;
  onSessionMayHaveChanged: () => void;
};

export default function MarkScreen({
  session,
  choir,
  initialToken,
  onSessionMayHaveChanged,
}: Props) {
  const myMemberId = choir.member_id;
  const [result, setResult] = useState<AttendanceResponse | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  /* The wait the server asked for, and how many times the code has been got
     wrong on this screen. Both die with the screen: neither is a fact about
     the person, only about the last few minutes. */
  const [lockout, setLockout] = useState<number | null>(null);
  const [attempts, setAttempts] = useState(0);
  const [busy, setBusy] = useState(false);
  // Kept apart from busy: while the scanner is waiting, typing the digits has
  // to stay open. It is the way out of a scanner that is not going to answer.
  const [scanning, setScanning] = useState(false);
  const [digits, setDigits] = useState('');
  /** Fetched only once a mark exists: nothing else on this screen needs it. */
  const [stats, setStats] = useState<MyAttendance | null>(null);
  const consumedToken = useRef<string | null>(null);
  const marked = result !== null || (session?.my_mark ?? null) !== null;

  // Who is already here, shown only to somebody already marked. The held rows
  // ride the same rows_version contract as the regent's roster: a ref rather
  // than state, because rebuilding the poll callback restarts the interval.
  const [feed, setFeed] = useState<ArrivalsFeed | null>(null);
  const heldFeed = useRef<{ version: string; rows: ArrivalRow[] } | null>(null);

  const pollFeed = useCallback(async () => {
    try {
      const fresh = await api.currentArrivals(heldFeed.current?.version);
      if (fresh.rows !== null) {
        heldFeed.current = { version: fresh.rows_version, rows: fresh.rows };
        setFeed(fresh);
      } else {
        // Unchanged: null means «keep yours», never an emptied room.
        setFeed({ ...fresh, rows: heldFeed.current?.rows ?? [] });
      }
    } catch {
      // The feed is a footnote to the mark; the next poll simply tries again.
    }
  }, []);
  usePoll(pollFeed, FEED_POLL_MS, marked);

  useEffect(() => {
    if (!marked || stats !== null) return;
    void api.myAttendance('rehearsal').then(setStats, () => {
      // The figures are a footnote to the mark: without them the mark still
      // shows, and there is nothing here worth an error message of its own.
    });
  }, [marked, stats]);

  async function mark(action: () => Promise<AttendanceResponse>): Promise<void> {
    setBusy(true);
    setFailure(null);
    try {
      const marked = await action();
      // The one moment this screen exists for, confirmed in the hand before
      // the eye finds the card.
      haptic('success');
      setResult(marked);
      setDigits('');
      // The mark now exists on the server. Re-read the session so that leaving
      // this tab and coming back shows it, rather than offering the check-in a
      // second time.
      onSessionMayHaveChanged();
    } catch (error) {
      haptic('error');
      // Counted on this side because nothing else counts it: the server's limit
      // is per minute and says nothing about how many times somebody has
      // mistyped. The figure is only ever shown beside the wait.
      setAttempts((count) => count + 1);
      const wait = error instanceof ApiError ? (error.failure?.retry_after_seconds ?? null) : null;
      setLockout(wait);
      setFailure(wait === null ? failureText(error, 'Не получилось, попробуй ещё раз') : null);
      // Six digits the server has refused are six digits nobody will type
      // again: the code rotates every half minute, so what is in the field is
      // stale the moment it comes back wrong, and the next attempt begins by
      // emptying it by hand. Only this failure - a code the server did not
      // know. The network dropping, or a gathering that closed underneath, says
      // nothing about the digits, and clearing them there would undo the
      // person's typing for a reason that was never theirs.
      if (error instanceof ApiError && error.failure?.error === 'invalid_code') setDigits('');
      if (error instanceof ApiError && error.failure?.error === 'session_closed') {
        onSessionMayHaveChanged();
      }
    } finally {
      setBusy(false);
    }
  }

  // Check in with a single gesture: they arrived by deep link, the token is
  // already in hand.
  useEffect(() => {
    if (initialToken === null || consumedToken.current === initialToken) return;
    consumedToken.current = initialToken;
    void mark(() => api.markByToken(initialToken));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialToken]);

  async function handleScan(): Promise<void> {
    // Held for the whole thing, including the wait. The scanner has no way to
    // report that camera access was refused - no event, no error - so scanQr
    // gives up after twelve seconds of its own accord. Without this the button
    // stayed live through all twelve: every impatient tap started another
    // twelve-second wait, on a screen showing no sign that anything was
    // happening, and the failures then arrived one after another.
    setScanning(true);
    try {
      const scanned = await scanQr();
      if (scanned === null) {
        setFailure('Сканер недоступен — наведи камеру телефона на код или введи цифры');
        return;
      }
      const token = tokenFromScannedText(scanned);
      if (token === null) {
        setFailure('Это не тот код');
        return;
      }
      await mark(() => api.markByToken(token));
    } finally {
      setScanning(false);
    }
  }

  if (session === undefined) {
    return <p className="hint">Смотрю, идёт ли что-нибудь…</p>;
  }

  /* The head every state of this screen carries: what is happening, in one
     uppercase line with the deadline named in it, and what it is called. It is
     here rather than in the confirmation because it is the rule a singer wants
     before they are late, not after.

     A named minute rather than «ЛЬГОТА 3 МИН»: since 2026-09-04 the grace is
     not simply added to the start, so the number alone no longer says when
     being on time stops - and that hour is the only thing anybody wanted from
     it. It drops out when the gathering was opened early enough to spend the
     whole grace on waiting: then the deadline is the start, already printed. */
  const head =
    session === null ? null : (
      <>
        <p className="label label--tone live-line">
          <span className="live-line-dot" />
          ИДЁТ С {formatTime(session.effective_start)}
          {hasLeeway(session) && <> · ВОВРЕМЯ ДО {formatTime(session.on_time_until)}</>}
        </p>
        <h1 className="screen-title">{session.title ?? words(session.kind).titled}</h1>
      </>
    );

  if (result !== null) {
    return (
      <>
        {head}
        <section className="mark card--toned" data-status={result.status}>
          {/* The person's own face, nodding once as the card arrives. It is the
              one moment in the app where the face answers something somebody
              has just done rather than reporting what they have done all month,
              and it is why the whole channel is worth having. */}
          <Avatar
            eyes={choir.avatar_eyes}
            color={choir.avatar_color}
            rate={stats?.attendance_rate_30d ?? null}
            size={AVATAR_CARD}
            move="nod"
            mine
            streak={stats?.streak ?? 0}
          />
          <span className="mark-said">
            <span className="label label--tone">Отметка есть</span>
            <p className="mark-fact">
              {result.status === 'late'
                ? `Опоздание ${result.late_minutes ?? 0} мин, в ${formatTime(result.marked_at)}`
                : `Вовремя, в ${formatTime(result.marked_at)}`}
            </p>
            <p className="mark-quiet">{streakLine(stats, result)}</p>
          </span>
        </section>
        <PeerPlate />
        {feed !== null && (
          <Arrivals feed={feed} myMemberId={myMemberId} myStreak={stats?.streak ?? 0} />
        )}
        {stats !== null && <Tally stats={stats} />}
      </>
    );
  }

  const mine = session?.my_mark ?? null;

  // Already marked, and the screen has to say so on every visit. The card above
  // is this screen's own memory of a tap and dies with it: a singer who checked
  // in, looked at the schedule and came back was offered the check-in again, and
  // the regent - marked by the very act of opening - was offered it always.
  if (mine !== null && mine.status !== 'absent') {
    return (
      <>
        {head}
        <section className="mark card--toned" data-status={mine.status}>
          {/* No nod on a revisit: the card is the screen's memory of a mark
              rather than the answer to one, and a face that jumps every time
              somebody looks at the schedule and comes back is fidgeting. */}
          <Avatar
            eyes={choir.avatar_eyes}
            color={choir.avatar_color}
            rate={stats?.attendance_rate_30d ?? null}
            size={AVATAR_CARD}
            mine
            streak={stats?.streak ?? 0}
          />
          <span className="mark-said">
            <span className="label label--tone">Отметка есть</span>
            <p className="mark-fact">{markedFact(mine)}</p>
            <p className="mark-quiet">{withStreak(markOrigin(mine, session), stats)}</p>
          </span>
        </section>
        {/* Under the card and over the feed, in both marked states. It is the
            thing this screen can do now that it could not before, and the feed
            below is the thing it could always do. */}
        <PeerPlate />
        {feed !== null && (
          <Arrivals feed={feed} myMemberId={myMemberId} myStreak={stats?.streak ?? 0} />
        )}
        {stats !== null && <Tally stats={stats} />}
      </>
    );
  }

  // A manual absence is sticky on the server: checking in over it is refused.
  // Saying so beats a button that answers «Регент записал тебе пропуск».
  if (mine !== null) {
    return (
      <>
        {head}
        <section className="refusal card--toned card--toned--outline" data-tone="danger">
          {/* «Пропуск» and not «не был», and «записал тебе» and not «отметил
              тебя отсутствующим»: the app has one word for this value now, and
              the sentence it stood in was gendered on top of it. */}
          <p className="label label--tone">Отмечен пропуск</p>
          <p className="refusal-fact">
            {mine.marked_at === null
              ? 'Регент записал тебе пропуск'
              : `Регент записал тебе пропуск в ${formatTime(mine.marked_at)}`}
          </p>
          {/* The one refusal with no way forward in it, so it says who has the
              way forward. The attempt is journalled and shows on the regent's
              panel as a conflict, which is why walking up to the regent is not a
              shot in the dark - they already know. */}
          <p className="refusal-note">
            Изменить это может только регент. Подойди и скажи — кодом перебить нельзя.
          </p>
        </section>
      </>
    );
  }

  return (
    <>
      {head}

      {/* The digits first and the scanner second, which is the order the frames
          draw and the opposite of what this screen used to do. Both paths are
          equal by the product's own rule, and the one that always works is the
          one the regent is saying out loud: a QR on a stand thirty rows away is
          not aimable, and a phone held up in front of it blocks it for
          everybody behind. */}
      <div className="entry">
        <p className="entry-label">Введи шесть цифр, которые называет регент</p>
        <CodeInput
          value={digits}
          length={NUMERIC_LENGTH}
          refused={failure !== null}
          onChange={(next) => {
            setDigits(next);
            // The refusal was about the code that was sent, and the moment a
            // digit changes it is about a code that no longer exists. Without
            // this the six edges stayed red under the digits of the next
            // attempt, which says the next attempt has already failed.
            setFailure(null);
            // The sixth digit sends the code by itself - FND-1590, the one
            // departure from the old app here, asked for after cutover. Only
            // on a change a person made: six digits a failure left behind are
            // not a change, so nothing here re-sends them in a loop, and a
            // letter typed into a full field cleans back to the same six.
            if (next.length === NUMERIC_LENGTH && next !== digits && !busy) {
              void mark(() => api.markByCode(next));
            }
          }}
        />
        {/* On screen from the first digit, and before it - disabled rather than
            absent. It used to appear once something had been typed, which moved
            the scanner, the failure line and everything under them while the
            keyboard was up and the eye was on the cells. */}
        <button
          className="btn btn--outline"
          disabled={busy || digits.length === 0}
          onClick={() => void mark(() => api.markByCode(digits))}
        >
          Отметиться по коду
        </button>
        <p className="label divider">или</p>
        <button className="btn btn--outline" onClick={handleScan} disabled={busy || scanning}>
          {scanning ? 'Открываю сканер…' : 'Сканировать QR камерой'}
        </button>
      </div>

      {lockout !== null && <Lockout seconds={lockout} attempts={attempts} />}
      {failure !== null && <p className="failure">{failure}</p>}
      {feed !== null && (
        <Arrivals feed={feed} myMemberId={myMemberId} myStreak={stats?.streak ?? 0} />
      )}
    </>
  );
}
